/* @ngInject */

export default ($q, $rootScope, logger, dateTimeService, authenticationDataFactory) => {
  /**
   * Check user credentials.
   *
   * @param {Object} [_params]
   * @param {String} [_params.email] - User E-Mail.
   * @param {String} [_params.password] - User Password.
   * @param {String} [_params.totp] - User TOTP.
   *
   * @returns {Promise}
   */
  function credentialsCheck(_params) {
    var deferred = $q.defer();

    logger.log('authenticationService', 'credentialsCheck', 'Resolving DataService');
    authenticationDataFactory.credentialsCheck(_params).then(function (payload) {
      logger.log('authenticationService', 'credentialsCheck', 'Resolved ' + payload);
      deferred.resolve(payload);
    });

    return deferred.promise;
  }

  /**
   * Clear user credentials.
   *
   * @returns {Promise}
   */
  function credentialsClear() {
    var deferred = $q.defer();

    logger.log('authenticationService', 'credentialsClear', 'Resolving DataService');
    authenticationDataFactory.credentialsClear().then(function (payload) {
      logger.log('authenticationService', 'credentialsClear', 'Resolved ' + payload);
      deferred.resolve(payload);
    });
    return deferred.promise;
  }

  /**
   * Validate set user credentials.
   *
   * @returns {Promise}
   */
  function credentialsValidate() {
    var deferred = $q.defer();

    logger.log('authenticationService', 'credentialsValidate', 'Resolving DataService');
    authenticationDataFactory.credentialsValidate().then(function (payload) {
      logger.log('authenticationService', 'credentialsValidate', 'Resolved ' + payload);
      deferred.resolve(payload);
    });

    return deferred.promise;
  }

  /**
   * Check if user is administrator.
   *
   * #param {String} [_id]
   *
   * @returns {Boolean}
   */
  function isAdministrator(_id) {
    if (!isAuthenticated()) {
      return false;
    }
    var idAccount = _id || $rootScope.CurrentAccount.id;
    return (
      this.isOwner(idAccount) ||
      $rootScope.CurrentOrganization.idAdministrators.indexOf(idAccount) > -1
    );
  }

  /**
   * Check if user is manager.
   *
   * #param {String} [_id]
   *
   * @returns {Boolean}
   */
  function isManager(_id) {
    if (!isAuthenticated()) {
      return false;
    }
    var idAccount = _id || $rootScope.CurrentAccount.id;
    return (
      this.isAdministrator(idAccount) &&
      $rootScope.CurrentOrganization.idManagers.indexOf(idAccount) > -1
    );
  }

  /**
   * Check if user is broker.
   *
   * #param {String} [_id]
   *
   * @returns {Boolean}
   */
  function isBroker(_id) {
    if (!isAuthenticated()) {
      return false;
    }
    var idAccount = _id || $rootScope.CurrentAccount.id;
    return (
      (!$rootScope.CurrentOrganization.isSubscribed() ||
        $rootScope.CurrentOrganization.isBrokerSubscribed() ||
        ($rootScope.CurrentOrganization.isSubscribed() &&
          $rootScope.hasBrokerToolsEnabled())) &&
      ($rootScope.CurrentOrganization.idBrokers.indexOf(idAccount) > -1 ||
        $rootScope.CurrentOrganization.idOwner === idAccount)
    );
  }

  /**
   * Check if user is mentor.
   *
   * #param {String} [_id]
   *
   * @returns {Boolean}
   */
  function isMentor(_id) {
    if (!isAuthenticated()) {
      return false;
    }
    var idAccount = _id || $rootScope.CurrentAccount.id;
    return (
      (!$rootScope.CurrentOrganization.isSubscribed() ||
        $rootScope.CurrentOrganization.isBrokerSubscribed() ||
        ($rootScope.CurrentOrganization.isSubscribed() &&
          $rootScope.hasBrokerToolsEnabled())) &&
      ($rootScope.CurrentOrganization.idMentors.indexOf(idAccount) > -1 ||
        $rootScope.CurrentOrganization.idOwner === idAccount)
    );
  }

  /**
   * Check if current user is authenticated.
   *
   * @returns {Boolean}
   */
  function isAuthenticated() {
    return authenticationDataFactory.isAuthenticated();
  }

  /**
   * Check if user is onboarded already.
   *
   * @returns {Boolean}
   */
  function isOnboarded() {
    return this.isAuthenticated() && $rootScope.CurrentAccount.isOnboarded();
  }

  /**
   * Check if user is owner.
   *
   * #param {String} [_id]
   *
   * @returns {Boolean}
   */
  function isOwner(_id) {
    if (!isAuthenticated()) {
      return false;
    }
    var idAccount = _id || $rootScope.CurrentAccount.id;
    return $rootScope.CurrentOrganization.idOwner === idAccount;
  }

  /**
   * Check if user is required to change the password..
   *
   * @returns {Boolean}
   */
  function isPasswordChangeRequired() {
    return (
      this.isAuthenticated() &&
      $rootScope.CurrentAccount.isPasswordChangeRequired(
        $rootScope.CurrentOrganization &&
          $rootScope.CurrentOrganization.permissions &&
          $rootScope.CurrentOrganization.permissions.passwordPolicy &&
          $rootScope.CurrentOrganization.permissions.passwordPolicy.expires
          ? $rootScope.CurrentOrganization.permissions.passwordPolicy.expires
          : 0
      )
    );
  }

  /**
   * Check if organization is suspended.
   *
   * @returns {Boolean}
   */
  function isSuspended() {
    var suspended = false;
    if (
      $rootScope.CurrentOrganization &&
      $rootScope.CurrentOrganization.subscription.subscriptions !== undefined &&
      ($rootScope.CurrentOrganization.getSubscriptionStatus() === 'Cancelled' ||
        $rootScope.CurrentOrganization.getSubscriptionStatus() === 'Unpaid' ||
        !$rootScope.CurrentOrganization.getSubscriptionStatus())
    ) {
      suspended = true;
    } else if (
      $rootScope.CurrentOrganization &&
      !$rootScope.CurrentOrganization.subscription.subscriptions &&
      dateTimeService
        .getMoment($rootScope.CurrentOrganization.timeStamp)
        .add(60, 'days')
        .endOf('day')
        .isBefore(dateTimeService.getMoment(dateTimeService.epoch()))
    ) {
      suspended = true;
    }
    return suspended;
  }

  /**
   * Change organization for user.
   *
   * @param {Object} [_params]
   * @param {Boolean} [_params.idOrganization] - Organization ID.
   *
   * @returns {Promise}
   */
  function organizationChange(_params) {
    var deferred = $q.defer();

    logger.log('authenticationService', 'organizationChange', 'Resolving DataService');
    authenticationDataFactory.organizationChange(_params).then(function (payload) {
      logger.log('authenticationService', 'organizationChange', 'Resolved ' + payload);
      deferred.resolve(payload);
    });

    return deferred.promise;
  }

  /**
   * Initialize Password Reset procedure for User.
   *
   * @param {Object} [_params]
   * @param {Boolean} [_params.email] - User E-Mail.
   *
   * @returns {Promise}
   */
  function passwordForgot(_params) {
    var deferred = $q.defer();

    logger.log('authenticationService', 'passwordForgot', 'Resolving DataService');
    authenticationDataFactory.passwordForgot(_params).then(function (payload) {
      logger.log('authenticationService', 'passwordForgot', 'Resolved ' + payload);
      deferred.resolve(payload);
    });

    return deferred.promise;
  }

  /**
   * Reset Password for User.
   *
   * @param {Object} [_params]
   * @param {Boolean} [_params.oldPassword] - User current Password.
   * @param {Boolean} [_params.newPassword] - User new Password.
   *
   * @returns {Promise}
   */
  function passwordChange(_params) {
    var deferred = $q.defer();

    logger.log('authenticationService', 'passwordChange', 'Resolving DataService');
    authenticationDataFactory.passwordChange(_params).then(function (payload) {
      logger.log('authenticationService', 'passwordChange', 'Resolved ' + payload);
      deferred.resolve(payload);
    });

    return deferred.promise;
  }

  /**
   * Retrieve user authentication token.
   *
   * @returns {Object}
   */
  function tokenGet() {
    return authenticationDataFactory.tokenGet();
  }

  /**
   * Initialize Lost Two-Factor device procedure for User.
   *
   * @param {Object} [_params]
   * @param {String} [_params.email] - User E-Mail.
   * @param {String} [_params.password] - User password.
   * @param {String} [_params.phone] - User partial phone number.
   *
   * @returns {Promise}
   */
  function twoFactorLost(_params) {
    var deferred = $q.defer();

    logger.log('authenticationService', 'twoFactorLost', 'Resolving DataService');
    authenticationDataFactory.twoFactorLost(_params).then(function (payload) {
      logger.log('authenticationService', 'twoFactorLost', 'Resolved ' + payload);
      deferred.resolve(payload);
    });

    return deferred.promise;
  }

  /**
   * Send SMS Code.
   *
   * @returns {Promise}
   */
  function twoFactorSMS() {
    var deferred = $q.defer();

    logger.log('authenticationService', 'twoFactorSMS', 'Resolving DataService');
    authenticationDataFactory.twoFactorSMS().then(function (payload) {
      logger.log('authenticationService', 'twoFactorSMS', 'Resolved ' + payload);
      deferred.resolve(payload);
    });

    return deferred.promise;
  }

  /**
   * Validate SMS Code.
   *
   * @returns {Promise}
   */
  function twoFactorSMSValidate(_params) {
    var deferred = $q.defer();

    logger.log('authenticationService', 'twoFactorSMSValidate', 'Resolving DataService');
    authenticationDataFactory.twoFactorSMSValidate(_params).then(function (payload) {
      logger.log('authenticationService', 'twoFactorSMSValidate', 'Resolved ' + payload);
      deferred.resolve(payload);
    });

    return deferred.promise;
  }

  /**
   * Toggle TOTP for User.
   *
   * @returns {Promise}
   */
  function twoFactorToggle() {
    var deferred = $q.defer();

    logger.log('authenticationService', 'twoFactorToggle', 'Resolving DataService');
    authenticationDataFactory.twoFactorToggle().then(function (payload) {
      logger.log('authenticationService', 'twoFactorToggle', 'Resolved ' + payload);
      deferred.resolve(payload);
    });

    return deferred.promise;
  }

  /**
   * Validate TOTP for User.
   *
   * @param {Object} [_params]
   * @param {Boolean} [_params.totp] - User current TOTP.
   *
   * @returns {Promise}
   */
  function twoFactorValidate(_params) {
    var deferred = $q.defer();

    logger.log('authenticationService', 'twoFactorValidate', 'Resolving DataService');
    authenticationDataFactory.twoFactorValidate(_params).then(function (payload) {
      logger.log('authenticationService', 'twoFactorValidate', 'Resolved ' + payload);
      deferred.resolve(payload);
    });

    return deferred.promise;
  }

  var service = {
    credentialsCheck: credentialsCheck,
    credentialsClear: credentialsClear,
    credentialsValidate: credentialsValidate,
    isAdministrator: isAdministrator,
    isManager: isManager,
    isBroker: isBroker,
    isMentor: isMentor,
    isAuthenticated: isAuthenticated,
    isOnboarded: isOnboarded,
    isOwner: isOwner,
    isPasswordChangeRequired: isPasswordChangeRequired,
    isSuspended: isSuspended,
    organizationChange: organizationChange,
    passwordForgot: passwordForgot,
    passwordChange: passwordChange,
    tokenGet: tokenGet,
    twoFactorLost: twoFactorLost,
    twoFactorSMS: twoFactorSMS,
    twoFactorSMSValidate: twoFactorSMSValidate,
    twoFactorToggle: twoFactorToggle,
    twoFactorValidate: twoFactorValidate
  };
  return service;
};
